import * as Sentry from '@sentry/browser';
import { User } from '../../types';
import { ILogger } from './Logger';

Sentry.registerSpanErrorInstrumentation();

export default class SentryLogger implements ILogger {
  static create({
    sessionId = '',
    DSN = '',
    release = '',
    environment = '',
  }: {
    DSN: string;
    release: string;
    sessionId: string;
    environment: string;
  }) {
    Sentry.init({
      dsn: DSN,
      release,
      environment,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
          maskAllInputs: false,
          networkDetailAllowUrls: ['.traceair.net'],
          mutationLimit: 1000000000000,
          mutationBreadcrumbLimit: 3000,
          minReplayDuration: 15000,
        }),
      ],
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 1.0,
      replaysOnErrorSampleRate: 1.0,
    });

    return new SentryLogger(sessionId);
  }

  constructor(sessionId: string) {
    this.setupTags(sessionId);
    window.addEventListener('error', (e) => this.exception(e.error));
    window.addEventListener('unhandledrejection', (e) => this.exception(e.reason));
  }

  setupTags(sessionId: string) {
    Sentry.setTag('sessionId', sessionId);

    // Docs: https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
    // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/ban-ts-comment
    // @ts-ignore
    const connection = navigator.connection;

    if (connection) {
      const { downlinkMax, downlink, rtt, effectiveType, type } = connection;

      Sentry.setTag('connectionType', type);
      Sentry.setTag('connectionEffectiveType', effectiveType);
      Sentry.setTag('connectionDownlink', downlink);
      Sentry.setTag('connectionDownlinkMax', downlinkMax);
      Sentry.setTag('connectionRtt', rtt);
    }
  }

  log(message: string, payload: { [key: string]: unknown } = {}) {
    payload = normalize(payload);
    this.setExtraContext(payload);
    Sentry.addBreadcrumb({ message, data: payload, level: 'log' });
  }

  info(message: string, payload: { [key: string]: unknown } = {}) {
    payload = normalize(payload);
    this.setExtraContext(payload);
    Sentry.addBreadcrumb({ message, data: payload, level: 'info' });
  }

  warn(message: string, payload: { [key: string]: unknown } = {}) {
    payload = normalize(payload);
    this.setExtraContext(payload);
    Sentry.addBreadcrumb({ message, data: payload, level: 'warning' });
  }

  error(message: string, payload: { [key: string]: unknown } = {}) {
    payload = normalize(payload);
    this.setExtraContext(payload);
    Sentry.captureMessage(message, 'error');
  }

  exception(exception: Error, payload: { [key: string]: unknown } = {}) {
    payload = normalize(payload);
    this.setExtraContext(payload);
    Sentry.captureException(exception);
  }

  setUserContext(userInfo: User) {
    Sentry.setUser(userInfo);
  }

  setExtraContext(contextData: { [key: string]: unknown }) {
    Sentry.setExtras(contextData);
  }

  captureBreadcrumb(message: string, data: object) {
    Sentry.addBreadcrumb({ message, data });
  }
}

function normalize(payload: { [key: string]: unknown }) {
  if (payload instanceof Error) {
    return { ...payload };
  }

  return payload;
}
